import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminService } from '../../services/adminService';
import { Search, Plus, Edit, Trash2, Filter, Download, X, Users as UsersIcon, Save, BadgeCheck, ShieldCheck, Image as ImageIcon } from 'lucide-react';

const Users = () => {
  const currentUser = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const isAdmin = Boolean(
    currentUser?.currentRole === 'admin' ||
    currentUser?.role === 'admin' ||
    (Array.isArray(currentUser?.roles) && currentUser.roles.includes('admin'))
  );
  const [activeTab, setActiveTab] = useState('users'); // users | kyc
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTLChangeModal, setShowTLChangeModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserForTLChange, setSelectedUserForTLChange] = useState(null);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    roles: [],
    status: 'active',
    teamLeaderId: '',
    serviceAdvisorId: '',
    dialerId: '',
    customerType: 'unregistered'
  });

  const [editingUser, setEditingUser] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [changingTL, setChangingTL] = useState(false);
  const [newTeamLeadId, setNewTeamLeadId] = useState('');

  const availableRoles = [
    'admin', 'mechanic', 'telecaller', 'teamlead', 'supervisor', 'partner',
    'serviceexecutive', 'serviceadvisor', 'customer', 'marketing', 'accounts', 'tlmanager'
  ];

  const { data: users = [], isLoading, refetch } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: AdminService.getAllUsers,
  });

  const { data: kycRows = [], isLoading: isKycLoading, refetch: refetchKyc } = useQuery({
    queryKey: ['adminTelecallerKyc'],
    queryFn: AdminService.getTelecallerKyc,
    enabled: isAdmin && activeTab === 'kyc',
  });

  // Derived lists: keep them memoized (avoid setState loop)
  const teamLeaders = useMemo(() => {
    return (users || [])
      .filter((u) => u.roles?.includes('teamlead'))
      .map((u) => ({ id: u.id, name: u.name, email: u.email }));
  }, [users]);

  const serviceAdvisors = useMemo(() => {
    return (users || [])
      .filter((u) => u.roles?.includes('serviceadvisor'))
      .map((u) => ({ id: u.id, name: u.name, email: u.email }));
  }, [users]);

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone?.includes(searchQuery);
    
    const matchesRole = roleFilter === 'all' || user.roles?.some(r => r?.toLowerCase() === roleFilter.toLowerCase());
    
    return matchesSearch && matchesRole;
  });

  const [kycSearch, setKycSearch] = useState('');
  const [kycStatusFilter, setKycStatusFilter] = useState('all'); // all | verified | pending | missing
  const [savingKyc, setSavingKyc] = useState({});

  const filteredKyc = useMemo(() => {
    const q = kycSearch.trim().toLowerCase();
    return (kycRows || []).filter((row) => {
      const matchesSearch =
        !q ||
        (row.name || '').toLowerCase().includes(q) ||
        (row.email || '').toLowerCase().includes(q) ||
        (row.phone || '').includes(q);

      const hasAadhaar = Boolean(row.aadhaar_front_image_path) && Boolean(row.aadhaar_back_image_path);
      const isVerified = Boolean(row.is_verified);
      const status =
        !hasAadhaar ? 'missing'
        : isVerified ? 'verified'
        : 'pending';

      const matchesStatus = kycStatusFilter === 'all' || status === kycStatusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [kycRows, kycSearch, kycStatusFilter]);

  const openDoc = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSaveKyc = async (row, next) => {
    setSavingKyc((prev) => ({ ...prev, [row.user_id]: true }));
    try {
      const res = await AdminService.updateTelecallerKyc(row.user_id, next);
      if (!res.success) {
        alert(res.error || 'Failed to update KYC');
        return;
      }
      await refetchKyc();
    } finally {
      setSavingKyc((prev) => ({ ...prev, [row.user_id]: false }));
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      alert('Please fill in all required fields');
      return;
    }

    if (newUser.roles.length === 0) {
      alert('Please select at least one role');
      return;
    }

    if (newUser.roles.includes('telecaller') && !newUser.teamLeaderId) {
      alert('Please assign a team leader for telecaller role');
      return;
    }

    setCreatingUser(true);
    try {
      const result = await AdminService.createUser(newUser);
      if (result.success) {
        alert('User created successfully!');
        setShowCreateModal(false);
        setNewUser({
          name: '', email: '', phone: '', password: '',
          roles: [], status: 'active', teamLeaderId: '',
          serviceAdvisorId: '', dialerId: '', customerType: 'unregistered'
        });
        refetch();
      } else {
        alert(result.error || 'Failed to create user');
      }
    } catch (error) {
      alert('Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser || editingUser.roles.length === 0) {
      alert('Please select at least one role');
      return;
    }

    setUpdatingUser(true);
    try {
      const result = await AdminService.updateUser(editingUser.id, {
        name: editingUser.name,
        email: editingUser.email,
        phone: editingUser.phone,
        status: editingUser.status,
        roles: editingUser.roles,
        dialer_user_id: editingUser.dialerId || null
      });
      if (result.success) {
        alert('User updated successfully!');
        setShowEditModal(false);
        setEditingUser(null);
        refetch();
      } else {
        alert(result.error || 'Failed to update user');
      }
    } catch (error) {
      alert('Failed to update user');
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      const result = await AdminService.deleteUser(selectedUser.id);
      if (result.success) {
        alert('User deleted successfully');
        setShowDeleteConfirm(false);
        setSelectedUser(null);
        refetch();
      } else {
        alert(result.error || 'Failed to delete user');
      }
    } catch (error) {
      alert('Failed to delete user');
    }
  };

  const handleChangeTeamLead = async () => {
    if (!selectedUserForTLChange || !newTeamLeadId) {
      alert('Please select both a user and a new team lead');
      return;
    }

    setChangingTL(true);
    try {
      const result = await AdminService.changeTeamLead(
        selectedUserForTLChange.id,
        newTeamLeadId,
        currentUser?.id || null // changedBy (uuid)
      );
      if (result.success) {
        alert(`Team Lead changed successfully for ${selectedUserForTLChange.name}`);
        setShowTLChangeModal(false);
        setSelectedUserForTLChange(null);
        setNewTeamLeadId('');
        refetch();
      } else {
        alert(result.error || 'Failed to change team lead');
      }
    } catch (error) {
      alert('Failed to change team lead');
    } finally {
      setChangingTL(false);
    }
  };

  const toggleRole = (role, isEdit = false) => {
    if (isEdit && editingUser) {
      setEditingUser({
        ...editingUser,
        roles: editingUser.roles.includes(role)
          ? editingUser.roles.filter(r => r !== role)
          : [...editingUser.roles, role]
      });
    } else {
      setNewUser({
        ...newUser,
        roles: newUser.roles.includes(role)
          ? newUser.roles.filter(r => r !== role)
          : [...newUser.roles, role]
      });
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: '#dc2626',
      mechanic: '#7c3aed',
      telecaller: '#ea580c',
      teamlead: '#0891b2',
      supervisor: '#be185d',
      partner: '#059669',
      serviceexecutive: '#f59e0b',
      serviceadvisor: '#8b5cf6',
    };
    return colors[role?.toLowerCase()] || '#6b7280';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: '#059669',
      inactive: '#dc2626',
      suspended: '#f59e0b',
    };
    return colors[status?.toLowerCase()] || '#059669';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage all system users</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            <Download size={20} />
            <span>Export</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
          >
            <Plus size={20} />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl p-2 shadow-sm border border-gray-200 inline-flex gap-2">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
            activeTab === 'users' ? 'bg-primary-500 text-white' : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          Users
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveTab('kyc')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === 'kyc' ? 'bg-primary-500 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            KYC
          </button>
        )}
      </div>

      {/* Stats */}
      {activeTab === 'users' && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Showing</p>
              <p className="text-2xl font-bold text-gray-900">{filteredUsers.length}</p>
            </div>
            <UsersIcon size={24} className="text-primary-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
            </div>
            <UsersIcon size={24} className="text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {users.filter(u => u.status === 'active').length}
              </p>
            </div>
            <UsersIcon size={24} className="text-blue-500" />
          </div>
        </div>
      </div>
      )}

      {/* Filters */}
      {activeTab === 'users' && (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Roles</option>
            {availableRoles.map(role => (
              <option key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>
      )}

      {/* Users Table */}
      {activeTab === 'users' && (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roles</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-primary-600 font-semibold text-sm">
                          {user.name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{user.email || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{user.phone || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.roles?.map((role, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 text-xs font-medium rounded text-white"
                          style={{ backgroundColor: getRoleColor(role) }}
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className="px-2 py-1 text-xs font-medium rounded text-white"
                      style={{ backgroundColor: getStatusColor(user.status) }}
                    >
                      {user.status || 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          setEditingUser({ ...user, dialerId: user.dialer_user_id || '' });
                          setShowEditModal(true);
                        }}
                        className="text-primary-600 hover:text-primary-800"
                      >
                        <Edit size={18} />
                      </button>
                      {(user.roles?.includes('telecaller') || user.roles?.includes('serviceexecutive')) && (
                        <button
                          onClick={() => {
                            setSelectedUserForTLChange(user);
                            setNewTeamLeadId('');
                            setShowTLChangeModal(true);
                          }}
                          className="text-green-600 hover:text-green-800"
                        >
                          <UsersIcon size={18} />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDeleteConfirm(true);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <UsersIcon size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No users found</p>
          </div>
        )}
      </div>
      )}

      {/* KYC Tab */}
      {activeTab === 'kyc' && isAdmin && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search telecallers..."
                  value={kycSearch}
                  onChange={(e) => setKycSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <select
                value={kycStatusFilter}
                onChange={(e) => setKycStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="missing">Missing Docs</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {isKycLoading ? (
              <div className="p-8 text-center text-gray-600">Loading KYC...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telecaller</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">KYC</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Docs</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission %</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredKyc.map((row) => {
                      const hasAadhaar = Boolean(row.aadhaar_front_image_path) && Boolean(row.aadhaar_back_image_path);
                      const status =
                        !hasAadhaar ? { label: 'Missing', color: 'bg-red-100 text-red-700' }
                        : row.is_verified ? { label: 'Verified', color: 'bg-green-100 text-green-700' }
                        : { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' };

                      return (
                        <tr key={row.user_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-primary-600 font-semibold text-sm">
                                  {(row.name || 'T').charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{row.name || 'N/A'}</div>
                                <div className="text-sm text-gray-500">{row.email || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{row.phone || ''}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${status.color}`}>
                              {status.label}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              {row.kyc_updated_at ? new Date(row.kyc_updated_at).toLocaleString() : ''}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={() => openDoc(row.aadhaar_front_image_path)}
                                disabled={!row.aadhaar_front_image_path}
                                className={`inline-flex items-center gap-2 text-sm font-semibold ${
                                  row.aadhaar_front_image_path ? 'text-primary-600 hover:text-primary-800' : 'text-gray-400'
                                }`}
                              >
                                <ImageIcon size={16} /> Aadhaar Front
                              </button>
                              <button
                                onClick={() => openDoc(row.aadhaar_back_image_path)}
                                disabled={!row.aadhaar_back_image_path}
                                className={`inline-flex items-center gap-2 text-sm font-semibold ${
                                  row.aadhaar_back_image_path ? 'text-primary-600 hover:text-primary-800' : 'text-gray-400'
                                }`}
                              >
                                <ImageIcon size={16} /> Aadhaar Back
                              </button>
                              <button
                                onClick={() => openDoc(row.pan_image_path)}
                                disabled={!row.pan_image_path}
                                className={`inline-flex items-center gap-2 text-sm font-semibold ${
                                  row.pan_image_path ? 'text-primary-600 hover:text-primary-800' : 'text-gray-400'
                                }`}
                              >
                                <ImageIcon size={16} /> PAN (optional)
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              defaultValue={row.commission_percent ?? ''}
                              onChange={(e) => {
                                row.__commissionDraft = e.target.value;
                              }}
                              className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                              placeholder="e.g. 5"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                defaultChecked={Boolean(row.is_verified)}
                                onChange={(e) => {
                                  row.__verifiedDraft = e.target.checked;
                                }}
                              />
                              <span className="text-sm text-gray-700">Verified</span>
                            </label>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() =>
                                handleSaveKyc(row, {
                                  is_verified: typeof row.__verifiedDraft === 'boolean' ? row.__verifiedDraft : Boolean(row.is_verified),
                                  commission_percent: row.__commissionDraft ?? row.commission_percent ?? null,
                                })
                              }
                              disabled={savingKyc[row.user_id]}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
                            >
                              {savingKyc[row.user_id] ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"></div>
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <ShieldCheck size={16} /> Save
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {filteredKyc.length === 0 && (
                  <div className="text-center py-12">
                    <BadgeCheck size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">No telecaller KYC records found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Create New User</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Roles *</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {availableRoles.map(role => (
                    <button
                      key={role}
                      onClick={() => toggleRole(role)}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                        newUser.roles.includes(role)
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
                {newUser.roles.length === 0 && (
                  <p className="text-red-500 text-sm mt-2">⚠️ Please select at least one role</p>
                )}
              </div>

              {newUser.roles.includes('telecaller') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team Leader *</label>
                  <select
                    value={newUser.teamLeaderId}
                    onChange={(e) => setNewUser({ ...newUser, teamLeaderId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select Team Leader</option>
                    {teamLeaders.map(tl => (
                      <option key={tl.id} value={tl.id}>{tl.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {newUser.roles.includes('customer') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer Type</label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setNewUser({ ...newUser, customerType: 'unregistered' })}
                      className={`flex-1 px-4 py-2 rounded-lg border ${
                        newUser.customerType === 'unregistered'
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      Unregistered
                    </button>
                    <button
                      onClick={() => setNewUser({ ...newUser, customerType: 'registered' })}
                      className={`flex-1 px-4 py-2 rounded-lg border ${
                        newUser.customerType === 'registered'
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      Registered
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={creatingUser}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center space-x-2"
                >
                  {creatingUser ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      <span>Create User</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Edit User</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={editingUser.name || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={editingUser.status || 'active'}
                  onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Roles *</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {availableRoles.map(role => (
                    <button
                      key={role}
                      onClick={() => toggleRole(role, true)}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                        editingUser.roles?.includes(role)
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditUser}
                  disabled={updatingUser}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center space-x-2"
                >
                  {updatingUser ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"></div>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Delete User</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{selectedUser.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Lead Change Modal */}
      {showTLChangeModal && selectedUserForTLChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Change Team Lead</h2>
              <button onClick={() => setShowTLChangeModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Change team lead for <strong>{selectedUserForTLChange.name}</strong>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">New Team Lead</label>
              <select
                value={newTeamLeadId}
                onChange={(e) => setNewTeamLeadId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select Team Lead</option>
                {teamLeaders.map(tl => (
                  <option key={tl.id} value={tl.id}>{tl.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowTLChangeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeTeamLead}
                disabled={changingTL || !newTeamLeadId}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {changingTL ? 'Changing...' : 'Change Team Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
